import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  addOrganizationPolicyExceptionFromAlert,
  addUserPolicyExceptionFromAlert,
} from '@kirin/api-client/api/policy-exceptions';
import type {
  AddPolicyExceptionFromAlert,
  AddFromAlertResult,
} from '@kirin/api-client/contracts/policy-exceptions';
import { apiClient } from '@/lib/api-client';
import { invalidatePackageListState } from './invalidatePackageListState';

/**
 * Scope of an add-from-alert action. Mirrors the alert action buttons: an
 * organization-shared list (owner/manager) or the current user's personal
 * (singleton) list.
 */
export type AddFromAlertScope =
  | { kind: 'organization'; organizationId: string }
  | { kind: 'account' };

/**
 * Mutation for the "Add to Allow/Block List" button on alerts (KIR-3558).
 *
 * Reuses the shared Allow/Block persistence via the account/organization
 * `from-alert` endpoints. On a real add it invalidates the matching policy
 * exceptions cache so the Policies screen reflects the new entry immediately;
 * the two "stop" outcomes (`already_on_list` / `covered_by_broad_rule`) change
 * nothing, so no invalidation is needed for them.
 *
 * @param scope - Organization or account scope.
 * @param policyKey - The Allow/Block list policy the entity maps to.
 */
export function useAddPolicyExceptionFromAlert(
  scope: AddFromAlertScope,
  policyKey: string | undefined
): UseMutationResult<AddFromAlertResult, Error, AddPolicyExceptionFromAlert> {
  const queryClient = useQueryClient();

  return useMutation<AddFromAlertResult, Error, AddPolicyExceptionFromAlert>({
    mutationFn: async (data: AddPolicyExceptionFromAlert) => {
      if (!policyKey) throw new Error('Policy key is required');
      if (scope.kind === 'organization') {
        return addOrganizationPolicyExceptionFromAlert(
          apiClient,
          scope.organizationId,
          policyKey,
          data
        );
      }
      return addUserPolicyExceptionFromAlert(apiClient, policyKey, data);
    },
    onSuccess: result => {
      if (result.status !== 'added' || !policyKey) return;
      const queryKey =
        scope.kind === 'organization'
          ? [
              'policyExceptions',
              'organization',
              scope.organizationId,
              policyKey,
            ]
          : ['policyExceptions', 'user', policyKey];
      void queryClient.invalidateQueries({ queryKey });
      invalidatePackageListState(queryClient, policyKey);
    },
  });
}
